import { isFuture } from "date-fns"
import { useGetCourseLessonStartDatesQuery } from "../../generated/graphql"

// Strukturelles Minimum, damit sowohl BookerAttendeeInput (Wizard) als auch
// der GetUpdatedBookerQuery-Attendee (Account) hineinpassen.
type AttendeeWithBookings = {
    firstname: string
    bookings?: Array<{ canceled?: unknown; courseLessons: Array<{ id: string }> } | null> | null
}

const uniqueValues = <T,>(array: Array<T>): Array<T> => Array.from(new Set(array))

// Stornierte Buchungen sperren nicht: Ein Teilnehmer, dessen einzige (begonnene)
// Buchung storniert wurde, muss bearbeitbar/löschbar bleiben.
const lessonIdsOf = (attendee: AttendeeWithBookings | null | undefined): string[] =>
    attendee?.bookings?.flatMap((booking) => (!booking || booking.canceled ? [] : booking.courseLessons.map(({ id }) => id))) ?? []

/**
 * Sammelt die Kurslektions-IDs ALLER Teilnehmer einer Liste. Als gemeinsame
 * Query-Variablen an useAttendeeEditLock übergeben, dedupliziert Apollo die
 * identischen Queries aller Zeilen zu EINEM Request pro Liste (statt einem
 * Request pro Teilnehmer, früher sogar mit ids: [] für buchungslose Zeilen).
 */
export const collectCourseLessonIds = (attendees: Array<AttendeeWithBookings | null | undefined> | null | undefined): string[] =>
    uniqueValues((attendees ?? []).flatMap((attendee) => lessonIdsOf(attendee)))

/**
 * Sperr-Logik für Teilnehmer-Aktionen: gesperrt, sobald der Teilnehmer in
 * Kurse gebucht ist, deren Kurslektionen bereits begonnen haben
 * (startDateTime nicht mehr in der Zukunft). Zukünftige Buchungen sperren nicht.
 *
 * HINWEIS: Die Query bewusst NICHT per `skip`/LazyQuery unterdrücken — beide
 * Varianten ließen in SelectAttendeeTable.behavior die parallelen Age-Check-
 * Queries im MockedProvider dauerhaft hängen (Apollo 3.11.8, mehrfach
 * unabhängig reproduziert). Die Bündelung über allCourseLessonIds löst das
 * Request-Problem stattdessen per Deduplizierung.
 */
export const useAttendeeEditLock = (attendee: AttendeeWithBookings, allCourseLessonIds?: string[]) => {
    const ownCourseLessonIdSet = new Set(lessonIdsOf(attendee))
    const hasLessons = ownCourseLessonIdSet.size > 0

    // Fail-safe: Die EIGENEN Lektions-IDs sind IMMER Teil der Query-Variablen —
    // eine unvollständige (oder leere) Caller-Liste darf die Sperrprüfung nicht
    // aushebeln (?? greift bei [] nicht, und der Set-Filter unten würde nicht
    // abgefragte Lektionen stillschweigend verwerfen). Enthält die Caller-Liste
    // die eigenen IDs (Normalfall), bleiben die Variablen zeilenübergreifend
    // identisch und Apollo dedupliziert weiterhin auf einen Request pro Liste.
    const { data, error } = useGetCourseLessonStartDatesQuery({
        variables: { ids: uniqueValues([...(allCourseLessonIds ?? []), ...ownCourseLessonIdSet]) },
    })
    const blockingCoursesText = uniqueValues(
        data?.courseLessons
            .filter(({ id, startDateTime }) => ownCourseLessonIdSet.has(id) && isFuture(new Date(startDateTime)) === false)
            .map(({ course }) => course.prefixedCourseNumber) ?? []
    ).join(", ")

    const loading = hasLessons && data === undefined && !error
    // Fail-closed: Solange die Sperrentscheidung aussteht (Query lädt oder ist
    // fehlgeschlagen), bleibt die Aktion gesperrt — sonst ließe sich die Sperre
    // für bereits begonnene Kurse durch schnelles Klicken umgehen.
    const blocked = blockingCoursesText !== "" || loading || (hasLessons && error !== undefined)

    return {
        blocked,
        loading,
        editTooltip: blocked
            ? `${attendee.firstname} ist in die folgenden Kurse ${blockingCoursesText} gebucht und kann deshalb nicht bearbeitet werden.`
            : "",
        deleteTooltip: blocked
            ? `${attendee.firstname} ist in die folgenden Kurse ${blockingCoursesText} gebucht und kann deshalb nicht gelöscht werden.`
            : "",
    }
}
